import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Upload, Loader2, ExternalLink, Pencil, RefreshCw, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { http } from "@/lib/api";
import { assetUrl } from "@/lib/branding";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function PreviewUploader({ value, onChange }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const url = assetUrl(value);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await http.post("/uploads?kind=cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.id);
      toast.success("Preview image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-testid="landing-preview-uploader">
      <label className="block text-xs text-muted-foreground">Preview image (PNG / JPEG / WebP)</label>
      <div className="relative flex h-40 items-center justify-center overflow-hidden rounded-lg border border-dashed border-white/[0.12] bg-[#0A0A0A]">
        {url ? (
          <img src={url} alt="landing preview" className="h-full w-full object-cover object-top" />
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-muted-foreground/60">
            <ImageIcon className="h-6 w-6" />
            <span className="text-[11px]">No preview image yet</span>
          </div>
        )}
        {busy && <div className="absolute inset-0 flex items-center justify-center bg-black/60"><Loader2 className="h-5 w-5 animate-spin text-white" /></div>}
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current?.click()}
          data-testid="landing-preview-asset-upload" className="gap-2 border-white/[0.12]">
          <Upload className="h-3.5 w-3.5" /> {value ? "Replace preview" : "Upload preview"}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => onChange("")}
            data-testid="landing-preview-asset-remove" className="gap-2 text-muted-foreground hover:text-[#EF4444]">
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground/70">Shown in the library and when picking this landing in an offer filter.</p>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="landing-preview-asset-input" />
    </div>
  );
}

export function LandingPreview({ previewFileId, height = "h-64", testId }) {
  const src = previewFileId ? assetUrl(previewFileId) : "";
  return (
    <div className={`flex items-center justify-center overflow-hidden rounded-lg border border-white/[0.08] bg-[#0A0A0A] ${height}`} data-testid={testId}>
      {src ? (
        <img src={src} alt="landing preview" loading="lazy"
          className="h-full w-full object-cover object-top" />
      ) : (
        <div className="flex flex-col items-center gap-1.5 text-muted-foreground/60">
          <ImageIcon className="h-6 w-6" />
          <span className="text-[11px]">No preview image</span>
        </div>
      )}
    </div>
  );
}

function AddOrReplaceDialog({ open, onOpenChange, landing }) {
  const qc = useQueryClient();
  const [name, setName] = useState(landing?.name || "");
  const [file, setFile] = useState(null);
  const [previewId, setPreviewId] = useState(landing?.preview_file_id || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  React.useEffect(() => { if (open) { setName(landing?.name || ""); setFile(null); setPreviewId(landing?.preview_file_id || ""); } }, [open, landing]);
  const submit = async () => {
    if (!landing && !file) { toast.error("Select a .zip archive"); return; }
    setBusy(true);
    try {
      if (landing) {
        if (file) {
          const fd = new FormData(); fd.append("file", file);
          await http.put(`/filter-landings/${landing.id}/zip`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        }
        const patch = {};
        if (name && name !== landing.name) patch.name = name;
        if ((previewId || "") !== (landing.preview_file_id || "")) patch.preview_file_id = previewId || "";
        if (Object.keys(patch).length) await http.patch(`/filter-landings/${landing.id}`, patch);
        toast.success("Landing updated");
      } else {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("name", name || file.name.replace(/\.zip$/i, ""));
        if (previewId) fd.append("preview_file_id", previewId);
        await http.post("/filter-landings", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Landing added");
      }
      qc.invalidateQueries({ queryKey: ["filter-landings"] });
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/[0.1] bg-[#141417]" data-testid="landing-form-dialog">
        <DialogHeader>
          <DialogTitle>{landing ? "Edit landing" : "Add landing"}</DialogTitle>
          <DialogDescription>Upload a ZIP archive of a static landing (extracted &amp; hosted for reuse) and a preview image shown across the app.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Landing name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Promo LP" data-testid="landing-name-input" className="border-white/[0.1] bg-[#0A0A0A]" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">{landing ? "Replace ZIP (optional)" : "ZIP archive"}</label>
            <input ref={fileRef} type="file" accept=".zip" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="landing-zip-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="gap-2 border-white/[0.12]" data-testid="landing-zip-btn">
              <Upload className="h-4 w-4" /> {file ? file.name : (landing ? "Choose new ZIP" : "Choose ZIP")}
            </Button>
          </div>
          <PreviewUploader value={previewId} onChange={setPreviewId} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/15">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="landing-save-btn" className="gap-2 bg-white text-black hover:bg-white/90">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {landing ? "Save" : "Add landing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ open, onOpenChange, landing, all }) {
  const qc = useQueryClient();
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const others = (all || []).filter((l) => l.id !== landing?.id);
  const used = (landing?.usage_count || 0) > 0;
  React.useEffect(() => { if (open) setTarget(""); }, [open]);
  const del = async () => {
    if (used && !target) { toast.error("Choose a replacement landing"); return; }
    setBusy(true);
    try {
      await http.delete(`/filter-landings/${landing.id}${used ? `?reassign_to=${target}` : ""}`);
      toast.success("Landing deleted");
      qc.invalidateQueries({ queryKey: ["filter-landings"] });
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/[0.1] bg-[#141417]" data-testid="landing-delete-dialog">
        <DialogHeader>
          <DialogTitle>Delete “{landing?.name}”</DialogTitle>
          <DialogDescription>
            {used
              ? `This landing is used in ${landing.usage_count} offer${landing.usage_count === 1 ? "" : "s"}. Pick another landing to migrate those filters to before deleting.`
              : "This landing is not used anywhere and can be deleted immediately."}
          </DialogDescription>
        </DialogHeader>
        {used && (
          others.length ? (
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="landing-reassign-select"
              className="h-10 w-full rounded-md border border-white/[0.1] bg-[#0A0A0A] px-2 text-sm text-foreground">
              <option value="">Select replacement landing…</option>
              {others.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          ) : (
            <p className="rounded-md border border-[#EF4444]/30 bg-[#EF4444]/10 p-2.5 text-xs text-[#EF4444]">
              No other landing exists to migrate to. Add another landing first, then delete this one.
            </p>
          )
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/15">Cancel</Button>
          <Button onClick={del} disabled={busy || (used && !others.length)} data-testid="landing-delete-confirm"
            className="gap-2 bg-[#EF4444] text-white hover:bg-[#EF4444]/90">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {used ? "Migrate & delete" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FilterLandingsLibrary() {
  const { data, isLoading } = useQuery({ queryKey: ["filter-landings"], queryFn: async () => (await http.get("/filter-landings")).data.items });
  const items = data || [];
  const [form, setForm] = useState({ open: false, landing: null });
  const [del, setDel] = useState({ open: false, landing: null });
  return (
    <div className="space-y-4" data-testid="filter-landings-library">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Filter Landings</h3>
          <p className="text-sm text-muted-foreground">ZIP landings reused by offer traffic filters. Upload once, use in any offer.</p>
        </div>
        <Button onClick={() => setForm({ open: true, landing: null })} data-testid="add-landing-btn" className="gap-2 bg-white text-black hover:bg-white/90">
          <Plus className="h-4 w-4" /> Add Landing
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/[0.12] py-14 text-center text-sm text-muted-foreground" data-testid="landings-empty">
          No landings yet. Click “Add Landing” to upload a ZIP.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((l) => (
            <div key={l.id} className="overflow-hidden rounded-xl border border-white/[0.08] bg-[#141417]" data-testid={`landing-card-${l.id}`}>
              <LandingPreview previewFileId={l.preview_file_id} height="h-40" testId={`landing-card-preview-${l.id}`} />
              <div className="space-y-2 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-foreground" title={l.name}>{l.name}</span>
                  <a href={`${BACKEND}${l.url}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-[#22C55E]" title="Open" data-testid={`landing-open-${l.id}`}><ExternalLink className="h-4 w-4" /></a>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-muted-foreground">{l.file_count} files · used in {l.usage_count} offer{l.usage_count === 1 ? "" : "s"}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setForm({ open: true, landing: l })} data-testid={`landing-edit-${l.id}`} className="rounded p-1.5 text-muted-foreground hover:bg-white/[0.06] hover:text-foreground"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setDel({ open: true, landing: l })} data-testid={`landing-delete-${l.id}`} className="rounded p-1.5 text-muted-foreground hover:bg-white/[0.06] hover:text-[#EF4444]"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddOrReplaceDialog open={form.open} landing={form.landing} onOpenChange={(o) => setForm((s) => ({ ...s, open: o }))} />
      <DeleteDialog open={del.open} landing={del.landing} all={items} onOpenChange={(o) => setDel((s) => ({ ...s, open: o }))} />
    </div>
  );
}
